import React from 'react';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';

// Spatial views of a survey at the approval stage: plan (easting/northing) and
// section (stepout/depth). Both draw the same two chains.
//
// The solid trace is the minimum-curvature solution through the currently
// ticked shots — the hole as it would be saved. The faint dashed trace is the
// solution through every shot, which is where the rejected stations live:
// because minimum curvature is cumulative, a rejected shot has no position in
// the approved chain at all, so plotting it against the approved coordinates
// would put it in the wrong frame. Showing both makes the effect of a rejection
// visible — drop a bad azimuth and the solid trace pulls away from the dashed.
const COLOURS = {
    approved: '#5470c6',
    failedButApproved: '#ee6666',
    rejected: '#bbbbbb',
    reference: '#cccccc'
};

// Axis insets around the plot area. The canvas is sized from these so that the
// *grid* comes out square, which is what actually makes the scales isometric —
// matching the data ranges only works if the pixels agree. A square canvas is
// not enough on its own, because the left and bottom insets carrying the axis
// labels and legend are larger than the right and top ones.
const INSET = { left: 65, right: 25, top: 45, bottom: 70 };

// A round-ish axis step of roughly a fifth of the span, so tick labels come out
// as 10s/20s/50s rather than whatever the data happens to span.
function niceStep(span) {
    const raw = span / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const normalised = raw / magnitude;
    const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
    return step * magnitude;
}

// These views must be isometric or the hole's shape is a lie: a 200 m step-out
// drawn on a 20 m-wide axis reads as a near-vertical hole, and a section view
// misreports the dip angle. ECharts has no "equal aspect" option, so the wider
// of the two spans is applied to both axes by hand.
//
// Both axes get an identical span (that's what keeps the aspect square) and a
// lower bound snapped to a whole number of steps, so the labels read as round
// numbers instead of the raw data bounds.
function squareExtent(points) {
    if (!points.length) return null;
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    // 10 m floor keeps a near-vertical hole from being drawn at absurd zoom.
    const rawSpan = Math.max(maxX - minX, maxY - minY, 10);
    const step = niceStep(rawSpan);
    const pad = rawSpan * 0.05;
    // Snap each origin below the padded data, then take a single span wide
    // enough for both axes. Derived from the data rather than from a centred
    // window, so the bounds always contain every station: a centred window can
    // be a step out and clip a point off the edge.
    const originX = Math.floor((minX - pad) / step) * step;
    const originY = Math.floor((minY - pad) / step) * step;
    const span = Math.max(
        Math.ceil((maxX + pad - originX) / step) * step,
        Math.ceil((maxY + pad - originY) / step) * step,
        step
    );
    return { minX: originX, maxX: originX + span, minY: originY, maxY: originY + span, step };
}

const SurveyTraceChart = ({
    title, xName, yName, project,
    approved, all, rejectedIndexes, qaFailIndexes, size = 380
}) => {
    const { t } = useTranslation();
    const approvedPoints = approved || [];
    const allPoints = all || [];

    // [x, y, depth] — depth rides along for the tooltip.
    const toPoint = (station) => [...project(station), station.Depth];

    // Rejected stations are read off the all-shots chain, which is the only
    // solution that has a position for them.
    const rejected = allPoints.filter(p => rejectedIndexes.has(p.Index));
    // Shots kept despite failing QA are worth calling out — approving one is a
    // deliberate act, so it should not look identical to a clean station.
    const failedButApproved = approvedPoints.filter(p => qaFailIndexes.has(p.Index));

    const approvedXY = approvedPoints.map(toPoint);
    const allXY = allPoints.map(toPoint);
    const extent = squareExtent([...approvedXY, ...allXY]);
    const showReference = rejected.length > 0;

    const tooltipFor = (label) => ({
        formatter: (params) => {
            const point = params.data;
            return `${label}<br/>${t('surveyTrace.depthValue', { depth: point[2].toFixed(2) })}`
                + `<br/>${xName}: ${point[0].toFixed(2)}  ${yName}: ${point[1].toFixed(2)}`;
        }
    });

    if (!approvedPoints.length && !allPoints.length) {
        return <p className="text-muted fst-italic">{t('surveyTrace.noPositions')}</p>;
    }

    const option = {
        title: { text: title, left: 'center' },
        tooltip: { trigger: 'item' },
        legend: { bottom: 0 },
        // Explicit width and height rather than margins: this is the square that
        // the equal data ranges are mapped onto.
        grid: { left: INSET.left, top: INSET.top, width: size, height: size },
        toolbox: { feature: { saveAsImage: {}, dataZoom: {} } },
        xAxis: {
            name: xName, nameLocation: 'middle', nameGap: 28,
            type: 'value', scale: true,
            min: extent ? extent.minX : undefined,
            max: extent ? extent.maxX : undefined,
            interval: extent ? extent.step : undefined,
            splitLine: { show: true }
        },
        yAxis: {
            name: yName, nameLocation: 'middle', nameGap: 45,
            type: 'value', scale: true,
            min: extent ? extent.minY : undefined,
            max: extent ? extent.maxY : undefined,
            interval: extent ? extent.step : undefined,
            splitLine: { show: true }
        },
        series: [
            ...(showReference ? [{
                name: t('surveyTrace.allShots'),
                type: 'line',
                data: allXY,
                showSymbol: false,
                silent: true,
                lineStyle: { color: COLOURS.reference, type: 'dashed', width: 1 },
                z: 1
            }] : []),
            {
                name: t('surveyTrace.approved'),
                type: 'line',
                data: approvedXY,
                symbol: 'circle',
                symbolSize: 7,
                itemStyle: { color: COLOURS.approved },
                lineStyle: { color: COLOURS.approved, width: 2 },
                tooltip: tooltipFor(t('surveyTrace.approved')),
                z: 3
            },
            {
                name: t('surveyTrace.approvedQaFail'),
                type: 'scatter',
                data: failedButApproved.map(toPoint),
                symbol: 'circle',
                symbolSize: 11,
                itemStyle: { color: COLOURS.failedButApproved },
                tooltip: tooltipFor(t('surveyTrace.approvedDespiteQa')),
                z: 4
            },
            {
                name: t('surveyTrace.rejected'),
                type: 'scatter',
                data: rejected.map(toPoint),
                symbol: 'circle',
                symbolSize: 9,
                itemStyle: { color: 'transparent', borderColor: COLOURS.rejected, borderWidth: 2 },
                tooltip: tooltipFor(t('surveyTrace.rejectedOnAllShots')),
                z: 2
            }
        ]
    };

    // Canvas sized to fit the square grid plus its insets, and centred: these
    // views have to stay square, so they do not stretch to fill a wide column.
    return (
        <ReactEcharts
            option={option}
            notMerge={true}
            style={{
                height: (size + INSET.top + INSET.bottom) + 'px',
                width: (size + INSET.left + INSET.right) + 'px',
                maxWidth: '100%',
                margin: '0 auto'
            }}
        />
    );
};

export const SurveyPlanChart = (props) => {
    const { t } = useTranslation();
    return (
        <SurveyTraceChart
            title={t('surveyTrace.planView')}
            xName={t('surveyTrace.easting')}
            yName={t('surveyTrace.northing')}
            project={s => [s.Easting, s.Northing]}
            {...props}
        />
    );
};

export const SurveySectionChart = (props) => {
    const { t } = useTranslation();
    return (
        <SurveyTraceChart
            title={t('surveyTrace.sectionView')}
            xName={t('surveyTrace.stepout')}
            yName={t('surveyTrace.depth')}
            // Elevation is stored positive downward, so it is negated to put the
            // collar at the top and the hole descending, matching ChartsTab.
            project={s => [s.Stepout, -s.Elevation]}
            {...props}
        />
    );
};
